/**
 * Duration formatting utilities for the scenes UI
 *
 * These helpers provide consistent duration formatting across the scenes list,
 * scene editor, and activation dialog.
 */

export type DurationUnit = 'seconds' | 'minutes' | 'hours';

/**
 * Format a duration in seconds to a short human-readable string.
 *
 * Examples:
 * - 45 → "45s"
 * - 75 → "1m 15s"
 * - 600 → "10m"
 * - 3660 → "1h 1m"
 *
 * Rules:
 * - Include seconds when under 2 minutes; otherwise minutes/hours is enough
 * - Keep output short and consistent
 */
export function formatDurationShort(seconds: number): string {
  if (seconds < 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  // Under 2 minutes: show seconds
  if (seconds < 120) {
    if (minutes > 0) {
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    }
    return `${secs}s`;
  }

  // 2 minutes or more: show minutes (and hours if applicable)
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
}

/**
 * Format time remaining until an ISO timestamp as a short duration string.
 *
 * Returns the same format as formatDurationShort:
 * - "45s", "1m 15s", "9m", "1h 10m", etc.
 *
 * Returns null if the time has already passed.
 */
export function formatTimeRemaining(untilIso: string): string | null {
  const now = new Date();
  const until = new Date(untilIso);
  const diffMs = until.getTime() - now.getTime();

  if (diffMs <= 0) return null;

  // Round up to nearest second for display
  const diffSeconds = Math.ceil(diffMs / 1000);
  return formatDurationShort(diffSeconds);
}

/**
 * Determine the most sensible unit for displaying a duration in seconds.
 *
 * - If durationSeconds < 60 → seconds
 * - Else if divisible by 3600 → hours
 * - Else → minutes
 */
export function inferBestUnit(durationSeconds: number): DurationUnit {
  if (durationSeconds < 60) {
    return 'seconds';
  }
  if (durationSeconds % 3600 === 0) {
    return 'hours';
  }
  return 'minutes';
}

/**
 * Convert a duration value + unit to seconds.
 */
export function toSeconds(value: number, unit: DurationUnit): number {
  switch (unit) {
    case 'seconds':
      return value;
    case 'minutes':
      return value * 60;
    case 'hours':
      return value * 3600;
  }
}

/**
 * Convert seconds to a value in the specified unit.
 * Returns a rounded integer value.
 */
export function fromSeconds(seconds: number, unit: DurationUnit): number {
  switch (unit) {
    case 'seconds':
      return Math.round(seconds);
    case 'minutes':
      return Math.round(seconds / 60);
    case 'hours':
      return Math.round(seconds / 3600);
  }
}

/**
 * Get the label for a duration unit (for dropdowns).
 */
export function getUnitLabel(unit: DurationUnit): string {
  switch (unit) {
    case 'seconds':
      return 'Seconds';
    case 'minutes':
      return 'Minutes';
    case 'hours':
      return 'Hours';
  }
}

/**
 * Duration unit options for select dropdowns.
 */
export const DURATION_UNIT_OPTIONS: { value: DurationUnit; label: string }[] = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
];
